import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { registerPlugin } from "@capacitor/core";

const AudioRoute = registerPlugin("AudioRoute");
const Proximity = registerPlugin("Proximity");

const socket = io("https://api.joss-serveur.duckdns.org");

function Call() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const savedCall = JSON.parse(localStorage.getItem("pendingIncomingCall") || "null");

  const member = location.state?.member || savedCall?.member || {};
  const mode = location.state?.mode || savedCall?.mode || "outgoing";

  const [incomingOffer, setIncomingOffer] = useState(
    location.state?.offer || savedCall?.offer || null
  );

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const targetUserId =
    mode === "incoming"
      ? savedCall?.fromUserId || member?.id || userId
      : member?.id || userId;

  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerRef = useRef(null);
  const ringbackRef = useRef(null);
  const ringbackIntervalRef = useRef(null);

  const [speakerOn, setSpeakerOn] = useState(false);
  const [accepted, setAccepted] = useState(mode !== "incoming");
  const [status, setStatus] = useState(
    mode === "incoming" ? "Appel entrant..." : "Préparation..."
  );

  const forceEarpiece = async () => {
    try {
      await AudioRoute.earpiece();
      setSpeakerOn(false);
      setTimeout(() => AudioRoute.earpiece().catch(() => {}), 300);
      setTimeout(() => AudioRoute.earpiece().catch(() => {}), 1000);
      setTimeout(() => AudioRoute.earpiece().catch(() => {}), 2000);
    } catch (err) {
      console.log("Audio route erreur:", err);
    }
  };

  const toggleSpeaker = async () => {
    try {
      if (speakerOn) {
        await forceEarpiece();
      } else {
        await AudioRoute.speaker();
        setSpeakerOn(true);
      }
    } catch (err) {
      console.log("Erreur switch audio:", err);
    }
  };

  const startProximity = async () => {
    try {
      await Proximity.start();
    } catch (err) {
      console.log("Proximity start erreur:", err);
    }
  };

  const stopProximity = async () => {
    try {
      await Proximity.stop();
    } catch (err) {
      console.log("Proximity stop erreur:", err);
    }
  };

  const startRingback = () => {
    if (ringbackIntervalRef.current) return;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioCtx = new AudioContextClass();
    ringbackRef.current = audioCtx;

    const playTone = () => {
      if (!ringbackRef.current) return;

      const now = audioCtx.currentTime;
      const osc1 = audioCtx.createOscillator();
      const osc2 = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc1.frequency.value = 440;
      osc2.frequency.value = 480;
      osc1.type = "sine";
      osc2.type = "sine";

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(audioCtx.destination);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.13, now + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 0.75);
      osc2.stop(now + 0.75);
    };

    playTone();
    ringbackIntervalRef.current = setInterval(playTone, 1600);
  };

  const stopRingback = () => {
    if (ringbackIntervalRef.current) {
      clearInterval(ringbackIntervalRef.current);
      ringbackIntervalRef.current = null;
    }

    if (ringbackRef.current) {
      ringbackRef.current.close().catch(() => {});
      ringbackRef.current = null;
    }
  };

  const createPeer = (stream, peerTargetUserId = targetUserId) => {
    const peer = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    stream.getTracks().forEach((track) => {
      peer.addTrack(track, stream);
    });

    peer.onicecandidate = (event) => {
      if (event.candidate && peerTargetUserId) {
        socket.emit("call:ice-candidate", {
          toUserId: peerTargetUserId,
          candidate: event.candidate,
        });
      }
    };

    peer.ontrack = async (event) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = event.streams[0];
        remoteAudioRef.current.setAttribute("playsinline", true);
        remoteAudioRef.current.muted = false;

        await remoteAudioRef.current.play().catch(() => {});
        await forceEarpiece();
      }
    };

    return peer;
  };

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      localStreamRef.current = stream;

      const peer = createPeer(stream, targetUserId);
      peerRef.current = peer;

      const callOffer = await peer.createOffer();
      await peer.setLocalDescription(callOffer);

      socket.emit("call:user", {
        toUserId: targetUserId,
        fromUser: currentUser,
        offer: callOffer,
      });

      setStatus("Appel en cours...");
      startRingback();
      await forceEarpiece();
    } catch (err) {
      console.error("Erreur appel:", err);
      stopRingback();
      alert("Erreur micro");
      navigate(-1);
    }
  };

  const acceptCall = async (offerToUse = incomingOffer, answerTargetId = targetUserId) => {
    try {
      stopRingback();

      if (!offerToUse) {
        setStatus("Connexion à l’appel...");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      localStreamRef.current = stream;

      const peer = createPeer(stream, answerTargetId);
      peerRef.current = peer;

      await peer.setRemoteDescription(new RTCSessionDescription(offerToUse));

      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);

      socket.emit("call:answer", {
        toUserId: answerTargetId,
        fromUserId: currentUser.id,
        answer,
      });

      localStorage.removeItem("pendingIncomingCall");
      setAccepted(true);
      setStatus("Connecté");

      await forceEarpiece();
      await startProximity();
    } catch (err) {
      console.error("Erreur acceptation:", err);
      alert("Erreur acceptation");
    }
  };

  const rejectCall = () => {
    socket.emit("call:reject", {
      toUserId: targetUserId,
      fromUserId: currentUser.id,
    });

    localStorage.removeItem("pendingIncomingCall");
    cleanup();
    navigate(-1);
  };

  const endCall = () => {
    socket.emit("call:end", {
      toUserId: targetUserId,
      fromUserId: currentUser.id,
    });

    localStorage.removeItem("pendingIncomingCall");
    cleanup();
    navigate(-1);
  };

  const cleanup = () => {
    stopRingback();
    stopProximity();

    AudioRoute.reset().catch(() => {});

    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    setSpeakerOn(false);
  };

  useEffect(() => {
    socket.on("call:incoming", ({ fromUser, offer }) => {
      setIncomingOffer(offer);

      localStorage.setItem(
        "pendingIncomingCall",
        JSON.stringify({
          mode: "incoming",
          member: fromUser,
          fromUserId: fromUser?.id,
          offer,
        })
      );

      setStatus("Appel entrant...");
      setAccepted(false);
    });

    socket.on("call:accepted", async ({ answer }) => {
      stopRingback();
      setStatus("Connecté");

      if (peerRef.current) {
        await peerRef.current.setRemoteDescription(
          new RTCSessionDescription(answer)
        );

        await forceEarpiece();
        await startProximity();
      }
    });

    socket.on("call:ice-candidate", async ({ candidate }) => {
      if (peerRef.current && candidate) {
        await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
    });

    socket.on("call:ended", () => {
      setStatus("Terminé");
      cleanup();
      localStorage.removeItem("pendingIncomingCall");
      setTimeout(() => navigate(-1), 1000);
    });

    socket.on("call:rejected", () => {
      setStatus("Refusé");
      cleanup();
      localStorage.removeItem("pendingIncomingCall");
      setTimeout(() => navigate(-1), 1000);
    });

    socket.emit("register", currentUser.id);
    socket.emit("call:get-pending", { userId: currentUser.id });

    if (mode !== "incoming") {
      setTimeout(() => startCall(), 800);
    }

    return () => {
      socket.off("call:incoming");
      socket.off("call:accepted");
      socket.off("call:ice-candidate");
      socket.off("call:ended");
      socket.off("call:rejected");
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName =
    member?.firstname || member?.lastname
      ? `${member?.firstname || ""} ${member?.lastname || ""}`.trim()
      : savedCall?.callerName || "Appel";

  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  return (
    <div style={styles.page}>
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <div style={styles.avatar}>{initials}</div>

      <h2 style={styles.name}>{displayName}</h2>

      <p style={styles.status}>{status}</p>

      {mode === "incoming" && !accepted && (
        <div style={styles.buttons}>
          <button style={styles.acceptButton} onClick={() => acceptCall()}>
            ✅ Accepter
          </button>

          <button style={styles.rejectButton} onClick={rejectCall}>
            ❌ Refuser
          </button>
        </div>
      )}

      {accepted && (
        <div style={styles.callControls}>
          <button style={styles.speakerButton} onClick={toggleSpeaker}>
            {speakerOn ? "🔊 Haut-parleur" : "👂 Écouteur"}
          </button>

          <button style={styles.endButton} onClick={endCall}>
            🔴 Raccrocher
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: 30,
    textAlign: "center",
    background:
      "radial-gradient(circle at top, rgba(214,181,109,0.18), transparent 35%), #071016",
    color: "white",
  },
  avatar: {
    width: 110,
    height: 110,
    borderRadius: "50%",
    margin: "75px auto 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(214,181,109,0.12)",
    border: "2px solid rgba(214,181,109,0.7)",
    color: "#d6b56d",
    fontSize: 34,
    fontWeight: 900,
    boxShadow: "0 0 35px rgba(214,181,109,0.25)",
  },
  name: {
    marginTop: 20,
    color: "#d6b56d",
  },
  status: {
    marginTop: 20,
    color: "rgba(255,255,255,0.7)",
  },
  buttons: {
    marginTop: 40,
    display: "flex",
    justifyContent: "center",
    gap: 14,
  },
  acceptButton: {
    padding: "14px 18px",
    borderRadius: 14,
    border: "none",
    background: "#22c55e",
    color: "white",
    fontWeight: 900,
  },
  rejectButton: {
    padding: "14px 18px",
    borderRadius: 14,
    border: "none",
    background: "#ef4444",
    color: "white",
    fontWeight: 900,
  },
  callControls: {
    marginTop: 40,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
  },
  speakerButton: {
    padding: "14px 20px",
    borderRadius: 999,
    border: "1px solid rgba(214,181,109,0.45)",
    background: "rgba(214,181,109,0.12)",
    color: "#d6b56d",
    fontWeight: 900,
  },
  endButton: {
    padding: "16px 22px",
    borderRadius: 999,
    border: "none",
    background: "#ef4444",
    color: "white",
    fontWeight: 900,
  },
};

export default Call;